class KomensiHeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.komensi-hero__slide'));
    if (this.slides.length < 2) return;

    this.track = this.querySelector('.komensi-hero__track');
    this.dots = Array.from(this.querySelectorAll('.komensi-hero__dot'));

    this.currentIndex = this.slides.findIndex((slide) => !slide.hidden);
    if (this.currentIndex < 0) this.currentIndex = 0;

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 6000;
    this.autoplayTimer = null;
    this.pauseReasons = new Set();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index, { manual: true }));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.goTo(this.currentIndex - 1, { manual: true });
      if (event.key === 'ArrowRight') this.goTo(this.currentIndex + 1, { manual: true });
    });

    this.addEventListener('mouseenter', () => this.pause('hover'));
    this.addEventListener('mouseleave', () => this.unpause('hover'));
    this.addEventListener('focusin', () => this.pause('focus'));
    this.addEventListener('focusout', () => this.unpause('focus'));

    this.visibilityHandler = () => {
      if (document.hidden) this.pause('hidden');
      else this.unpause('hidden');
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.setupDrag();

    if (this.autoplayEnabled && !this.reducedMotion) this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  goTo(index, { manual = false } = {}) {
    const total = this.slides.length;
    const nextIndex = (index + total) % total;
    if (nextIndex === this.currentIndex) return;

    this.slides[this.currentIndex].hidden = true;
    this.slides[this.currentIndex].classList.remove('is-active');
    if (this.dots[this.currentIndex]) this.dots[this.currentIndex].setAttribute('aria-selected', 'false');

    this.currentIndex = nextIndex;

    this.slides[this.currentIndex].hidden = false;
    this.slides[this.currentIndex].classList.add('is-active');
    if (this.dots[this.currentIndex]) this.dots[this.currentIndex].setAttribute('aria-selected', 'true');

    if (manual) this.resumeAutoplayIfEnabled();
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayTimer = window.setInterval(() => this.goTo(this.currentIndex + 1), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resumeAutoplayIfEnabled() {
    if (this.autoplayEnabled && !this.reducedMotion && this.pauseReasons.size === 0) {
      this.startAutoplay();
    }
  }

  pause(reason) {
    this.pauseReasons.add(reason);
    this.stopAutoplay();
  }

  unpause(reason) {
    this.pauseReasons.delete(reason);
    this.resumeAutoplayIfEnabled();
  }

  setupDrag() {
    const track = this.track;
    if (!track || !window.PointerEvent) return;

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let intent = null;
    let suppressClick = false;

    const activeSlide = () => this.slides[this.currentIndex];

    const settleTransform = () => {
      const slide = activeSlide();
      if (!slide) return;
      if (!this.reducedMotion) {
        slide.classList.add('is-settling');
        slide.addEventListener('transitionend', () => slide.classList.remove('is-settling'), { once: true });
      }
      slide.style.transform = '';
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      currentX = startX;
      intent = null;
      suppressClick = false;
    };

    const onPointerMove = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (intent === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        intent = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        if (intent === 'horizontal') {
          try {
            track.setPointerCapture(pointerId);
          } catch {
            /* pointer capture unavailable, drag still works via move/up listeners */
          }
          this.classList.add('is-dragging');
          this.pause('drag');
          suppressClick = true;
        }
      }

      if (intent !== 'horizontal') return;

      event.preventDefault();
      currentX = event.clientX;
      const slide = activeSlide();
      if (slide) slide.style.transform = `translateX(${currentX - startX}px)`;
    };

    const endDrag = (event) => {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      const wasHorizontal = intent === 'horizontal';
      const dx = currentX - startX;

      try {
        track.releasePointerCapture(pointerId);
      } catch {
        /* already released or never captured */
      }

      pointerId = null;
      intent = null;
      this.classList.remove('is-dragging');

      if (!wasHorizontal) return;

      const threshold = 60;
      const committed = Math.abs(dx) > threshold;

      this.pauseReasons.delete('drag');

      if (committed) {
        const slide = activeSlide();
        if (slide) {
          slide.classList.remove('is-settling');
          slide.style.transform = '';
        }
        this.goTo(this.currentIndex + (dx < 0 ? 1 : -1), { manual: true });
      } else {
        settleTransform();
        this.resumeAutoplayIfEnabled();
      }
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
        }
      },
      true,
    );
  }
}

if (!customElements.get('komensi-hero-slideshow')) {
  customElements.define('komensi-hero-slideshow', KomensiHeroSlideshow);
}
